import { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Basho from "./pages/Basho";
import BashoMap from "./pages/BashoMap";
import DailyHaiku from "./pages/DailyHaiku";
import YourHokku from "./pages/YourHokku";
import Rooms from "./pages/Rooms";
import RoomDetail from "./pages/RoomDetail";
import Profile from "./pages/Profile";
import KimonoRoom from "./pages/KimonoRoom";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";

function Footer() {
  return (
    <footer className="border-t border-border mt-20">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-baseline gap-2">
          <span className="hanko text-primary text-xl">俳</span>
          <span className="font-serif text-xl">Haiku House</span>
        </div>
        <p className="text-sm text-muted-foreground max-w-md font-serif italic">
          "The moon and sun are eternal travellers." — Matsuo Bashō
        </p>
        <a
          href="https://app.termly.io/policy-viewer/policy.html?policyUUID=e047bbf4-624d-43a9-af2f-e5f50ef45b5c"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-privacy-policy"
          className="text-sm text-muted-foreground hover:text-primary transition-colors ink-underline pb-0.5"
        >
          Privacy Policy
        </a>
      </div>
    </footer>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="max-w-3xl mx-auto px-6 py-24 text-muted-foreground">…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Shell() {
  const [theme, setTheme] = useState(() => localStorage.getItem("hh-theme") || "light");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("hh-theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return (
    <div className="App flex flex-col min-h-screen">
      <Navbar theme={theme} toggleTheme={toggleTheme} />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/basho" element={<Basho />} />
          <Route path="/map" element={<BashoMap />} />
          <Route path="/daily" element={<DailyHaiku />} />
          <Route path="/hokku" element={<YourHokku />} />
          <Route path="/rooms" element={<Rooms />} />
          <Route path="/rooms/:id" element={<RoomDetail />} />
          <Route path="/kimono" element={<KimonoRoom />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <Toaster position="bottom-right" theme={theme} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
